import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { trackEvent, analyticsPermitted } from "@/lib/analytics";
import { CHECKOUT_SESSION_ID_RE } from "@shared/purchaseEvent";
import { shouldFirePurchase, markPurchaseFired, purchaseParams } from "@/lib/purchaseEvent";

/**
 * Mount once at the app root. After a Stripe redirect (?checkout=success&session_id=cs_...) it asks the server what
 * that Checkout session really was and fires the GA4 `purchase` event from the answer: real amount, currency and
 * the session id as transaction_id. Test-mode, unpaid and zero-value sessions fire nothing. A reload or a revisit
 * of the same URL cannot fire twice (remembered in this browser across tabs, and GA4 also drops a repeated transaction_id). The
 * session id is removed from the address bar once read. Only the plan, tier, amount and currency are sent to GA.
 */
export function PurchaseAnalytics() {
  const sessionId = useRef<string | null>(null);
  if (sessionId.current === null && typeof window !== "undefined") {
    const id = new URLSearchParams(window.location.search).get("session_id") ?? "";
    sessionId.current = CHECKOUT_SESSION_ID_RE.test(id) && new URLSearchParams(window.location.search).get("checkout") === "success" ? id : "";
  }
  const id = sessionId.current || "";
  const q = trpc.billing.purchaseDetails.useQuery({ sessionId: id }, { enabled: !!id, retry: 1, refetchOnWindowFocus: false, staleTime: Infinity });

  useEffect(() => {
    const d = q.data;
    if (!id || !d) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
    const params = purchaseParams(d);
    if (!params || !analyticsPermitted() || !shouldFirePurchase(d.transactionId)) return;
    markPurchaseFired(d.transactionId);
    trackEvent("purchase", params);
  }, [q.data, id]);

  return null;
}
